import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFetcher } from "react-router-dom";

type BaseFetcher<T> = ReturnType<typeof useFetcher<T>>;
type FetcherLoadArgs<T> = Parameters<BaseFetcher<T>["load"]>;
export type FetcherWithLoadPromise<T> = Omit<BaseFetcher<T>, "load"> & {
  load: (...args: FetcherLoadArgs<T>) => Promise<BaseFetcher<T>>;
};
type PromiseResolveFn<T> = (result: BaseFetcher<T>) => void;

interface HookArg {
  key?: string;
}

export default function usePromiseLoadFetcher<T>({
  key,
}: HookArg = {}): FetcherWithLoadPromise<T> {
  const fetcher = useFetcher<T>(key ? { key } : undefined);

  const resolveFnRef = useRef<PromiseResolveFn<T>>(() => {});
  const isResolvedRef = useRef(false);

  const load = useCallback(
    (...args: FetcherLoadArgs<T>) => {
      return new Promise<BaseFetcher<T>>((resolve) => {
        resolveFnRef.current = resolve;
        isResolvedRef.current = false;
        void fetcher.load(...args);
        // callDecimal 401s throw and will be caught by a window listener
        // nothing else should intentionally throw
      });
    },
    [fetcher]
  );

  useEffect(() => {
    if (fetcher.state === "idle" && !isResolvedRef.current) {
      isResolvedRef.current = true;
      resolveFnRef.current(fetcher);
    }
  }, [fetcher]);

  return useMemo(() => ({ ...fetcher, load }), [fetcher, load]);
}
